"use client";

import { GE, GTMEvent } from ".constants/gtmEvents";
import { PORTALS, TPORTALS } from ".constants/portals";
import { usePortalContext } from "@cloai/uikit";
import { map } from "lodash";
import { useCallback, useMemo } from "react";

import { PhotoSettings, useGTMContext, useUndressContext } from "@/providers";

/**
 * @typedef {Object} HandlerConfig - Конфигурация обработчика
 * @property {GTMEvent} gtmEvent - Событие для GTM
 * @property {TPORTALS} portal - Ключ портала для открытия
 * @property {Array<PhotoSetting>} [settings] - Настройки для обновления
 */
type HandlerConfig = {
  gtmEvent: GTMEvent;
  settings?: Array<{
    key: keyof PhotoSettings;
    value: string;
  }>;
  portal: TPORTALS;
};

/**
 * Хук для обработки кликов по карточкам в стартовом экране
 * @returns {Object} Объект с обработчиками для каждого типа карточки
 */
export const useClickStart = () => {
  const { initialSettings, updatePhotoSetting, clickFileId } =
    useUndressContext();
  const { openPortal } = usePortalContext();
  const { sendGTM } = useGTMContext();

  /**
   * Конфигурация для всех обработчиков
   */
  const HANDLERS_CONFIG: Record<string, HandlerConfig> = useMemo(
    () => ({
      /**
       * Обработчик клика по карточке Video
       * 1. Отправляет ивент в метрику
       * 2. Открывает портал выбора клипов
       */
      video: {
        gtmEvent: GE.FLOW_START_CLICK_VIDEO_PLATE,
        portal: PORTALS.USER_FLOW_CLIPS,
      },

      /**
       * Обработчик клика по карточке Nude
       * 1. Отправляет ивент в метрику
       * 2. Сбрасывает позу на начальную
       * 3. Устанавливает костюм "Naked"
       * 4. Открывает портал выбора поз
       */
      nude: {
        gtmEvent: GE.FLOW_START_CLICK_NUDE_PLATE,
        settings: [
          { key: "pose", value: initialSettings.pose },
          { key: "costume", value: initialSettings.costume },
        ],
        portal: PORTALS.USER_FLOW_POSES,
      },

      /**
       * Обработчик клика по карточке Costume
       * 1. Отправляет ивент в метрику
       * 2. Сбрасывает позу на начальную
       * 3. Открывает портал выбора костюмов
       */
      costume: {
        gtmEvent: GE.FLOW_START_CLICK_COSTUME_PLATE,
        settings: [{ key: "pose", value: initialSettings.pose }],
        portal: PORTALS.USER_FLOW_COSTUMES,
      },

      /**
       * Обработчик клика по карточке Pose
       * 1. Отправляет ивент в метрику
       * 2. Устанавливает костюм "default"
       * 3. Открывает портал выбора поз
       */
      pose: {
        gtmEvent: GE.FLOW_START_CLICK_POSE_PLATE,
        settings: [{ key: "costume", value: "default" }],
        portal: PORTALS.USER_FLOW_POSES,
      },

      /**
       * Обработчик клика по карточке Prompt
       * 1. Отправляет ивент в метрику
       * 2. Сбрасывает позу на начальную
       * 3. Устанавливает режим "prompt"
       * 4. Открывает портал ввода промпта
       */
      prompt: {
        gtmEvent: GE.FLOW_START_CLICK_PROMPT_PLATE,
        settings: [
          { key: "pose", value: initialSettings.pose },
          { key: "costume", value: "prompt" },
        ],
        portal: PORTALS.USER_FLOW_PROMPT,
      },

      /**
       * Обработчик клика по карточке Collection
       * 1. Отправляет ивент в метрику
       * 2. Открывает портал выбора коллекций
       */
      collection: {
        gtmEvent: GE.FLOW_START_CLICK_COLLECTION_PLATE,
        portal: PORTALS.USER_FLOW_COLLECTIONS,
      },
    }),
    [initialSettings.costume, initialSettings.pose],
  );

  /**
   * Создаем базовый обработчик
   */
  const createHandler = useCallback(
    (config: HandlerConfig) => {
      const { gtmEvent, portal, settings = [] } = config;

      return () => {
        sendGTM({ event: gtmEvent });

        map(settings, ({ key, value }) => {
          updatePhotoSetting(clickFileId, key, value);
        });

        openPortal(portal);
      };
    },
    [clickFileId, openPortal, sendGTM, updatePhotoSetting],
  );

  /**
   * Создаем все обработчики на основе конфигурации
   */
  const handlers = useMemo(
    () => ({
      handleClickVideo: createHandler(HANDLERS_CONFIG.video),
      handleClickNude: createHandler(HANDLERS_CONFIG.nude),
      handleClickCostume: createHandler(HANDLERS_CONFIG.costume),
      handleClickPose: createHandler(HANDLERS_CONFIG.pose),
      handleClickPrompt: createHandler(HANDLERS_CONFIG.prompt),
      handleClickCollection: createHandler(HANDLERS_CONFIG.collection),
    }),
    [HANDLERS_CONFIG, createHandler],
  );

  return handlers;
};
